/**
 * OrderPage - Clean Shopify-inspired checkout
 * Route: /order/:sessionId
 */

import { useState, useEffect, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import * as Sentry from "@sentry/react";
import {
  useCart,
  useUpdateCartItem,
  useRemoveCartItem,
  useCreateOrder,
  useCustomerLookup,
  useSession,
} from "@/api/hooks";
import {
  formatCurrency,
  validateSAPhone,
  validateSAPostalCode,
  SA_PROVINCES,
  cn,
} from "@/lib/utils";
import { useBrandTheme } from "@/lib/brand-theme";
import { useTheme } from "@/lib/theme/ThemeProvider";
import { analytics, trackCartViewed, trackCheckoutStarted } from "@/lib/analytics";
import { analyticsApi } from "@/api/client";
import { Button } from "@/components/Button";
import { Card } from "@/components/Card";
import { Input } from "@/components/Input";
import { Select } from "@/components/Select";
import { LoadingSpinner } from "@/components/LoadingSpinner";
import { ErrorMessage } from "@/components/ErrorMessage";
import { CartItem } from "@/components/CartItem";
import type { ShippingInfo, CreateOrderRequest } from "@/types";
import { fetchRetailerConfig, type ShippingMethod } from "@/lib/supabase-api";

type Step = "cart" | "shipping" | "review";

interface ShippingFormData extends ShippingInfo {
  shipping_option_id: string;
  suburb?: string;
}

const initialFormData: ShippingFormData = {
  name: "",
  phone: "",
  email: "",
  address: "",
  suburb: "",
  city: "",
  postal_code: "",
  province: "",
  shipping_option_id: "",
};

// Breadcrumb component
function Breadcrumb({ currentStep }: { currentStep: Step }) {
  const stepOrder: Step[] = ["cart", "shipping", "review"];
  const currentIndex = stepOrder.indexOf(currentStep);
  const getStepClassName = (stepKey: Step) => {
    const stepIndex = stepOrder.indexOf(stepKey);
    if (stepIndex === currentIndex) return "active";
    if (stepIndex < currentIndex) return "completed";
    return "";
  };

  return (
    <nav className="checkout-breadcrumb mb-6">
      <span className={cn("checkout-breadcrumb-item", getStepClassName("cart"))}>Cart</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className={cn("checkout-breadcrumb-item", getStepClassName("shipping"))}>Shipping</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className={cn("checkout-breadcrumb-item", getStepClassName("review"))}>Review</span>
      <svg
        className="w-4 h-4 text-[var(--color-text-placeholder)]"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
      <span className="checkout-breadcrumb-item">Payment</span>
    </nav>
  );
}

// Secure checkout badge with theme toggle
function SecureBadge() {
  const { resolvedTheme, setTheme } = useTheme();

  const toggleTheme = () => {
    setTheme(resolvedTheme === "dark" ? "light" : "dark");
  };

  return (
    <div className="flex items-center gap-3">
      <div className="secure-badge">
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
          />
        </svg>
        <span>Secure checkout</span>
      </div>
      <button
        onClick={toggleTheme}
        className="p-2 rounded-lg hover:bg-[var(--color-surface-secondary)] transition-colors"
        aria-label={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
        title={`Switch to ${resolvedTheme === "dark" ? "light" : "dark"} mode`}
      >
        {resolvedTheme === "dark" ? (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
            />
          </svg>
        ) : (
          <svg
            className="w-5 h-5 text-[var(--color-text-secondary)]"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
            />
          </svg>
        )}
      </button>
    </div>
  );
}

export function OrderPage() {
  const navigate = useNavigate();
  const { sessionId } = useParams<{ sessionId: string }>();
  const { theme } = useBrandTheme();
  const { resolvedTheme } = useTheme();

  const [step, setStep] = useState<Step>("cart");
  const [formData, setFormData] = useState<ShippingFormData>(initialFormData);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof ShippingFormData, string>>>({});
  const [showSummary, setShowSummary] = useState(false);
  const [shippingData, setShippingData] = useState<ShippingMethod[]>([]);
  const [enforceShipping, setEnforceShipping] = useState(false)

  const { data: cart, isLoading: cartLoading, error: cartError } = useCart(sessionId || null);
  const { data: sessionData } = useSession(sessionId || null);
  // const { data: shippingData, isLoading: shippingLoading } = useShippingOptions();

  const updateItem = useUpdateCartItem();
  const removeItem = useRemoveCartItem();
  const createOrderMutation = useCreateOrder();
  const customerLookup = useCustomerLookup();

  const [hasCheckedReturningCustomer, setHasCheckedReturningCustomer] = useState(false);
  const hasTrackedCheckoutInitiated = useRef(false);
  // Messes with the order flow because it is cleared and then it tries to read it again
  // Clear shipping method from localStorage on mount
  // useEffect(() => {
  //   localStorage.removeItem("selectedShippingMethod");
  // }, []);

  // Track page view
  useEffect(() => {
    if (sessionId) {
      analytics.init(sessionId);
      analytics.pageView("order");
    }
  }, [sessionId]);

  // Track checkout_initiated when cart successfully loads
  useEffect(() => {
    async function trackCheckoutInitiated() {
      if (hasTrackedCheckoutInitiated.current) return;

      if (cart && !cartLoading && !cartError && sessionId && sessionData?.session?.retailer_id) {
        hasTrackedCheckoutInitiated.current = true;
        const response = await analyticsApi
          .track({
            session_id: sessionId,
            event: "checkout_initiated",
            properties: {
              retailer_id: sessionData.session.retailer_id,
              cents_cart_value: cart.subtotal_cents,
            },
          })
          .catch((error) => {
            console.error("Failed to track checkout_initiated:", error);
            // Reset flag on error so it can retry
            hasTrackedCheckoutInitiated.current = false;
          });
        console.log("response", response);
      }
    }
    trackCheckoutInitiated();
  }, [cart, cartLoading, cartError, sessionId, sessionData]);

  // Track cart viewed
  useEffect(() => {
    if (cart && cart.items.length > 0) {
      trackCartViewed(cart.item_count, cart.subtotal_cents);
    }
  }, [cart]);

  // Track checkout started when moving to shipping
  useEffect(() => {
    if (step === "shipping" && cart) {
      trackCheckoutStarted(cart.item_count, cart.subtotal_cents);
    }
  }, [step, cart]);

  // Track GTM checkout_started event when session has attribution
  useEffect(() => {
    if (sessionData?.session?.context?.attribution) {
      try {
        (window as any).dataLayer?.push({
          event: "checkout_started",
          link_id: sessionData.session.context.attribution.linkId,
          utm_source: sessionData.session.context.attribution.utmSource,
          utm_medium: sessionData.session.context.attribution.utmMedium,
          utm_campaign: sessionData.session.context.attribution.utmCampaign,
        });
      } catch (error) {
        console.error("GTM checkout_started error:", error);
        if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
          Sentry.captureException(error, {
            tags: { component: "OrderPage", action: "gtmCheckoutStarted" },
          });
        }
      }
    }
  }, [sessionData]);

  const handleUpdateQuantity = async (productId: string, quantity: number) => {
    if (!sessionId) return;
    try {
      await updateItem.mutateAsync({
        session_id: sessionId,
        product_id: productId,
        quantity,
      });
    } catch (error) {
      console.error("Failed to update cart item:", error);
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "updateCartItem", productId },
        });
      }
    }
  };

  const handleRemoveItem = async (productId: string) => {
    if (!sessionId) return;
    try {
      await removeItem.mutateAsync({
        session_id: sessionId,
        product_id: productId,
      });
    } catch (error) {
      console.error("Failed to remove cart item:", error);
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "removeCartItem", productId },
        });
      }
    }
  };

  const handleInputChange = (field: keyof ShippingFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: undefined }));
    }
    if (field === "phone") {
      setHasCheckedReturningCustomer(false);
    }
  };

  const handleShippingOptionChange = (option: {
    id: string;
    name: string;
    description: string;
    price_cents: number;
  }) => {
    setFormData((prev) => ({ ...prev, shipping_option_id: option.id }));
    if (formErrors.shipping_option_id) {
      setFormErrors((prev) => ({ ...prev, shipping_option_id: undefined }));
    }
    localStorage.setItem("selectedShippingMethod", JSON.stringify(option));
  };

  const handlePhoneBlur = async () => {
    const phone = formData.phone.trim();
    if (hasCheckedReturningCustomer) return;
    if (!validateSAPhone(phone)) return;
    if (!theme?.id) return;

    setHasCheckedReturningCustomer(true);

    try {
      const result = await customerLookup.mutateAsync({
        phoneNumber: phone,
        retailerId: theme.id,
      });

      if (!result.found || !result.shipping) return;

      setFormData((prev) => ({
        ...prev,
        name: result.shipping?.name || "",
        email: result.shipping?.email || "",
        address: result.shipping?.address || "",
        city: result.shipping?.city || "",
        postal_code: result.shipping?.postal_code || "",
        province: result.shipping?.province || "",
      }));
    } catch (error) {
      // Silently fail - convenience feature
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "customerLookup", phone },
        });
      }
    }
  };

  const validateForm = (): boolean => {
    // If enforceShipping is false, shipping is entirely optional
    // Skip all validations and allow proceeding to review
    if (!enforceShipping) {
      setFormErrors({});
      return true;
    }

    const errors: Partial<Record<keyof ShippingFormData, string>> = {};

    if (!formData.name.trim()) {
      errors.name = "Full name is required";
    }

    if (!formData.phone.trim()) {
      errors.phone = "Phone number is required";
    } else if (!validateSAPhone(formData.phone)) {
      errors.phone = "Enter a valid South African phone number";
    }

    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = "Enter a valid email address";
    }

    if (!formData.address.trim()) {
      errors.address = "Street address is required";
    }

    if (!formData.suburb?.trim()) {
      errors.suburb = "Suburb is required";
    }

    if (!formData.city.trim()) {
      errors.city = "City is required";
    }

    if (!formData.postal_code.trim()) {
      errors.postal_code = "Postal code is required";
    } else if (!validateSAPostalCode(formData.postal_code)) {
      errors.postal_code = "Enter a valid 4-digit postal code";
    }

    if (!formData.province) {
      errors.province = "Province is required";
    }

    if (enforceShipping && !formData.shipping_option_id) {
      errors.shipping_option_id = "Select a shipping method";
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleProceedToShipping = () => {
    setStep("shipping");
    window.scrollTo(0, 0);
  };

  const handleBackToCart = () => {
    setStep("cart");
    window.scrollTo(0, 0);
  };

  const handleProceedToReview = () => {
    if (!validateForm()) return;
    if (enforceShipping && !formData.shipping_option_id) {
      setFormErrors((prev) => ({
        ...prev,
        shipping_option_id: "Please select a shipping method to continue",
      }));
      return;
    }
    setStep("review");
    window.scrollTo(0, 0);
  };

  const handleBackToShipping = () => {
    setStep("shipping");
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const loadRetailerConfig = async () => {
      if (!theme?.id) return;
      try {
        const retailer = await fetchRetailerConfig(theme.id);
        const shippingMethods = Object.values(retailer?.config?.shipping_methods || {});

        setShippingData(shippingMethods);
        setEnforceShipping(retailer?.config?.enforce_shipping || false)

      } catch (error) {
        console.error("Failed to fetch retailer config", error);
      }
    };
    loadRetailerConfig();
  }, [theme?.id]);

  const handleSubmitOrder = async () => {
    if (!validateForm() || !sessionId || !cart) return;

    try {
      // Prepare order request for Edge Function API
      const orderRequest: CreateOrderRequest = {
        session_id: sessionId,
        shipping_info: {
          name: enforceShipping ? formData.name.trim() : 'Unknown',
          phone: enforceShipping ? formData.phone.trim() : 'Unknown',
          email: formData.email?.trim() || undefined,
          address: enforceShipping ? formData.address.trim(): 'Unknown',
          city: enforceShipping ? formData.city.trim() : 'Unknown',
          postal_code: enforceShipping ? formData.postal_code.trim() : 'Unknown',
          province: enforceShipping ? formData.province : 'Unknown',
        },
        shipping_option_id: formData.shipping_option_id,
      };

      console.log("orderRequest", orderRequest);

      const result = await createOrderMutation.mutateAsync(orderRequest);

      analytics.stepCompleted("shipping");

      // Navigate to payment page with order_id
      navigate(`/order/${sessionId}/pay?order_id=${result.order_id}`);
    } catch (error) {
      console.error("Failed to create order:", error);
      if (import.meta.env.VITE_SENTRY_ENVIRONMENT !== "development") {
        Sentry.captureException(error, {
          tags: { component: "OrderPage", action: "createOrder", sessionId },
        });
      }
    }
  };

  // Loading state
  if (cartLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg-primary)]">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // Error state
  if (cartError) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--color-bg-primary)]">
        <ErrorMessage
          title="Failed to load cart"
          message={cartError instanceof Error ? cartError.message : "An error occurred"}
          className="max-w-md"
        />
      </div>
    );
  }

  // Empty cart
  if (!cart || cart.items.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-[var(--color-bg-primary)]">
        <Card className="max-w-sm w-full text-center">
          <div className="empty-state py-4">
            <svg className="empty-state-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
                d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z"
              />
            </svg>
            <h2 className="text-[18px] font-semibold text-[var(--color-text-primary)]">
              Your cart is empty
            </h2>
            <p className="text-[14px] text-[var(--color-text-muted)] mt-2">
              No items in your cart at the moment.
            </p>
          </div>
        </Card>
      </div>
    );
  }

  const hasOutOfStockItems = cart.items.some((item) => !item.in_stock);
  const selectedShippingOption = Array.isArray(shippingData)
    ? shippingData?.find((_, idx) => `shipping-${idx}` === formData.shipping_option_id)
    : undefined;
  const shippingCents = selectedShippingOption?.price_cents || 0;
  const totalCents = cart.subtotal_cents + shippingCents;

  return (
    <div className="min-h-screen bg-(--color-bg-primary) flex flex-col justify-between gap-5">
      <div>
        {/* Header */}
        <header className="bg-[var(--color-surface-primary)] border-b border-[var(--color-surface-border-light)]">
          <div className="max-w-6xl mx-auto px-4 py-4">
            <div className="flex items-center justify-between flex-wrap-reverse">
              {theme?.logo_url ? (
                <img src={theme.logo_url} alt={theme.name} className="h-12 w-auto" />
              ) : (
                <div className="text-[18px] font-semibold text-[var(--color-text-primary)]">
                  {theme?.name || "Checkout"}
                </div>
              )}
              <SecureBadge />
            </div>
          </div>
        </header>

        <main className="max-w-6xl mx-auto px-4 py-8">
          <Breadcrumb currentStep={step} />
          <div className="lg:grid lg:grid-cols-[1fr_380px] lg:gap-4">
            {/* Main content */}
            <div>
              {step === "cart" ? (
                <>
                  {/* Cart section */}
                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Your cart ({cart.item_count} {cart.item_count === 1 ? "item" : "items"})
                    </h2>
                    <div>
                      {cart.items.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          onUpdateQuantity={handleUpdateQuantity}
                          onRemove={handleRemoveItem}
                          isUpdating={updateItem.isPending || removeItem.isPending}
                        />
                      ))}
                    </div>
                  </Card>

                  {hasOutOfStockItems && (
                    <div className="error-box mb-4">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">Some items are out of stock</p>
                        <p className="text-[12px] opacity-80 mt-0.5">
                          Remove out-of-stock items to continue.
                        </p>
                      </div>
                    </div>
                  )}

                  <Button
                    onClick={handleProceedToShipping}
                    disabled={hasOutOfStockItems}
                    fullWidth
                    size="lg"
                  >
                    Continue to shipping
                  </Button>
                </>
              ) : step === "shipping" ? (
                <>
                  {/* Shipping section */}
                  {enforceShipping && (
                  <Card className="mb-4">
                    <p className="text-[var(--color-text-primary)] text-sm">
                      All fields with an asterisk <span className="text-[var(--color-error)]">*</span> are required
                    </p>
                  </Card>
                  )}

                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Contact information {!enforceShipping && "(optional)"}
                    </h2>
                    <div className="space-y-4">
                      <Input
                        label="Full name"
                        value={formData.name}
                        onChange={(e) => handleInputChange("name", e.target.value)}
                        error={formErrors.name}
                        placeholder="John Doe"
                        required={enforceShipping ? true : false}
                      />
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="Phone number"
                          value={formData.phone}
                          onChange={(e) => handleInputChange("phone", e.target.value)}
                          onBlur={handlePhoneBlur}
                          error={formErrors.phone}
                          placeholder="082 123 4567"
                          type="tel"
                          required={enforceShipping ? true : false}
                        />
                        <Input
                          label="Email (optional)"
                          value={formData.email || ""}
                          onChange={(e) => handleInputChange("email", e.target.value)}
                          error={formErrors.email}
                          placeholder="john@example.com"
                          type="email"
                        />
                      </div>
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Shipping address {!enforceShipping && "(optional)"}
                    </h2>
                    <div className="space-y-4">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 ">
                        <Input
                          label="Street address"
                          value={formData.address}
                          onChange={(e) => handleInputChange("address", e.target.value)}
                          error={formErrors.address}
                          placeholder="123 Main Street, Unit 4"
                          required={enforceShipping ? true : false}
                        />
                        <Input
                          label="Suburb"
                          value={formData.suburb}
                          onChange={(e) => handleInputChange("suburb", e.target.value)}
                          error={formErrors.suburb}
                          placeholder="Benoni"
                          required={enforceShipping ? true : false}
                        />
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <Input
                          label="City"
                          value={formData.city}
                          onChange={(e) => handleInputChange("city", e.target.value)}
                          error={formErrors.city}
                          placeholder="Johannesburg"
                          required={enforceShipping ? true : false}
                        />
                        <Input
                          label="Postal code"
                          value={formData.postal_code}
                          onChange={(e) => handleInputChange("postal_code", e.target.value)}
                          error={formErrors.postal_code}
                          placeholder="2000"
                          required={enforceShipping ? true : false}
                        />
                      </div>
                      <Select
                        label="Province"
                        value={formData.province}
                        onChange={(e) => handleInputChange("province", e.target.value)}
                        error={formErrors.province}
                        required={enforceShipping ? true : false}
                      >
                        <option value="">Select province</option>
                        {SA_PROVINCES.map((province) => (
                          <option key={province} value={province}>
                            {province}
                          </option>
                        ))}
                      </Select>
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Shipping method {enforceShipping ? <span className="text-[var(--color-error)] ml-0.5">*</span> : "(optional)"}
                    </h2>
                    {Array.isArray(shippingData) ? (
                      <div className="space-y-3">
                        {shippingData.map((option, index) => {
                          const optionId = `shipping-${index}`;
                          return (
                            <label
                              key={index}
                              className={cn(
                                "shipping-option",
                                formData.shipping_option_id === optionId && "selected"
                              )}
                            >
                              <div className="shipping-option-radio" />
                              <input
                                type="radio"
                                name="shipping_option"
                                value={optionId}
                                checked={formData.shipping_option_id === optionId}
                                onChange={() =>
                                  handleShippingOptionChange({
                                    id: optionId,
                                    name: option.name,
                                    description: option.description,
                                    price_cents: option.price_cents,
                                  })
                                }
                                className="sr-only"
                              />
                              <div className="flex-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-[14px] font-medium text-[var(--color-text-primary)]">
                                    {option.name}
                                  </span>
                                  <span className="text-[14px] font-medium text-[var(--color-text-primary)] tabular-nums">
                                    {formatCurrency(option.price_cents)}
                                  </span>
                                </div>
                                <p className="text-[12px] text-[var(--color-text-muted)] mt-0.5">
                                  {option.description}
                                </p>
                              </div>
                            </label>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-[14px] text-[var(--color-text-muted)]">
                        No shipping options available
                      </p>
                    )}
                    {formErrors.shipping_option_id && (
                      <p className="mt-2 text-[12px] text-[var(--color-error)]">
                        {formErrors.shipping_option_id}
                      </p>
                    )}
                  </Card>

                  {/* {createOrder.error && (
                    <div className="error-box mb-4">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                      </svg>
                      <div>
                        <p className="font-medium">Failed to create order</p>
                        <p className="text-[12px] opacity-80 mt-0.5">
                          {createOrder.error instanceof Error ? createOrder.error.message : 'Please try again'}
                        </p>
                      </div>
                    </div>
                  )} */}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleBackToCart}
                      variant="accent"
                      className="sm:order-1 opacity-100"
                    >
                      Back to cart
                    </Button>
                    <Button
                      onClick={handleProceedToReview}
                      // disabled={!formData?.shipping_option_id}
                      fullWidth
                      size="lg"
                      className="sm:order-2"
                    >
                      Continue to review
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  {/* Review section */}
                  <Card className="mb-4">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)]">
                          Review your order
                        </h2>
                        <p className="text-[13px] text-[var(--color-text-muted)] mt-1">
                          Confirm your items and delivery details before continuing to payment.
                        </p>
                      </div>
                      <Button variant="ghost" size="sm" onClick={handleBackToShipping}>
                        Edit
                      </Button>
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Items
                    </h3>
                    <div>
                      {cart.items.map((item) => (
                        <CartItem
                          key={item.id}
                          item={item}
                          onUpdateQuantity={async () => { }}
                          onRemove={async () => { }}
                          isUpdating={false}
                          compact
                        />
                      ))}
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Delivery {!enforceShipping && 'Optional'}
                    </h3>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between gap-4">
                        <span className="text-[var(--color-text-secondary)]">Recipient</span>
                        <span className="text-[var(--color-text-primary)] font-medium text-right">
                          {formData.name || "—"}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-[var(--color-text-secondary)]">Phone</span>
                        <span className="text-[var(--color-text-primary)] font-medium tabular-nums text-right">
                          {formData.phone || "—"}
                        </span>
                      </div>
                      {formData.email && (
                        <div className="flex justify-between gap-4">
                          <span className="text-[var(--color-text-secondary)]">Email</span>
                          <span className="text-[var(--color-text-primary)] font-medium text-right">
                            {formData.email}
                          </span>
                        </div>
                      )}
                      <div className="divider" />
                      <div className="flex justify-between gap-4">
                        <span className="text-[var(--color-text-secondary)]">Address</span>
                        <span className="text-[var(--color-text-primary)] font-medium text-right">
                          {formData.address
                            ? `${formData.address}, ${formData.city}, ${formData.province} ${formData.postal_code}`
                            : "—"}
                        </span>
                      </div>
                      <div className="flex justify-between gap-4">
                        <span className="text-[var(--color-text-secondary)]">Shipping method</span>
                        <span className="text-[var(--color-text-primary)] font-medium text-right">
                          {selectedShippingOption
                            ? `${selectedShippingOption.name} (${formatCurrency(selectedShippingOption.price_cents)})`
                            : enforceShipping ? "—" : "Not selected (optional)"}
                        </span>
                      </div>
                    </div>
                  </Card>

                  <Card className="mb-4">
                    <h3 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                      Totals
                    </h3>
                    <div className="space-y-2 text-[14px]">
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                        <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                          {formatCurrency(cart.subtotal_cents)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Shipping</span>
                        <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                          {selectedShippingOption ? formatCurrency(shippingCents) : "—"}
                        </span>
                      </div>
                      <div className="divider" />
                      <div className="flex justify-between items-baseline">
                        <span className="text-[var(--color-text-primary)] font-semibold">
                          Total
                        </span>
                        <span className="price-total tabular-nums">
                          {formatCurrency(totalCents)}
                        </span>
                      </div>
                    </div>
                  </Card>

                  {createOrderMutation.error && (
                    <div className="error-box mb-4">
                      <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                      <div>
                        <p className="font-medium">Failed to create order</p>
                        <p className="text-[12px] opacity-80 mt-0.5">
                          {createOrderMutation.error instanceof Error
                            ? createOrderMutation.error.message
                            : "Please try again"}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      onClick={handleBackToShipping}
                      variant="accent"
                      className="sm:order-1 opacity-100"
                    >
                      Back to shipping
                    </Button>
                    <Button
                      onClick={() => handleSubmitOrder()}
                      disabled={enforceShipping && !formData.shipping_option_id}
                      isLoading={createOrderMutation.isPending}
                      fullWidth
                      size="lg"
                      className="sm:order-2"
                    >
                      Continue to payment
                    </Button>
                  </div>
                </>
              )}
            </div>

            {/* Order summary sidebar */}
            <div className="hidden lg:block">
              <div className="sticky top-8">
                <Card>
                  <h2 className="text-[16px] font-semibold text-[var(--color-text-primary)] mb-4">
                    Order summary
                  </h2>

                  {/* Products */}
                  <div className="border-b border-[var(--color-surface-border-light)] pb-4 mb-4">
                    {cart.items.map((item) => (
                      <CartItem
                        key={item.id}
                        item={item}
                        onUpdateQuantity={handleUpdateQuantity}
                        onRemove={handleRemoveItem}
                        isUpdating={false}
                        compact
                      />
                    ))}
                  </div>

                  {/* Totals */}
                  <div className="space-y-2 text-[14px]">
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                      <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                        {formatCurrency(cart.subtotal_cents)}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-[var(--color-text-secondary)]">Shipping</span>
                      <span className="text-[var(--color-text-primary)] font-medium tabular-nums">
                        {step !== "cart" && selectedShippingOption
                          ? formatCurrency(shippingCents)
                          : step === "cart"
                            ? "Calculated next"
                            : "—"}
                      </span>
                    </div>
                    {step !== "cart" && selectedShippingOption && (
                      <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                        {selectedShippingOption.name}
                      </p>
                    )}
                    <div className="divider" />
                    <div className="flex justify-between items-baseline">
                      <span className="text-[var(--color-text-primary)] font-semibold">Total</span>
                      <span className="price-total tabular-nums">
                        {formatCurrency(step === "cart" ? cart.subtotal_cents : totalCents)}
                      </span>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>

          {/* Mobile order summary toggle */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-[var(--color-surface-primary)] border-t border-[var(--color-surface-border-light)] p-4 z-50">
            <div className="flex items-center justify-between mb-3">
              <button
                onClick={() => setShowSummary(!showSummary)}
                className="flex items-center gap-2 text-[14px] text-[var(--color-secondary)]"
              >
                <svg
                  className={cn("w-4 h-4 transition-transform", showSummary && "rotate-180")}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                {showSummary ? "Hide" : "Show"} order summary
              </button>
              <span className="price-total tabular-nums">
                {formatCurrency(step === "cart" ? cart.subtotal_cents : totalCents)}
              </span>
            </div>

            {showSummary && (
              <div className="mb-4 max-h-60 overflow-y-auto border-t border-[var(--color-surface-border-light)] pt-4">
                {cart.items.map((item) => (
                  <CartItem
                    key={item.id}
                    item={item}
                    onUpdateQuantity={handleUpdateQuantity}
                    onRemove={handleRemoveItem}
                    isUpdating={false}
                    compact
                  />
                ))}
                <div className="divider" />
                <div className="space-y-2 text-[14px]">
                  <div className="flex justify-between">
                    <span className="text-[var(--color-text-secondary)]">Subtotal</span>
                    <span className="tabular-nums">{formatCurrency(cart.subtotal_cents)}</span>
                  </div>
                  {step !== "cart" && selectedShippingOption && (
                    <>
                      <div className="flex justify-between">
                        <span className="text-[var(--color-text-secondary)]">Shipping</span>
                        <span className="tabular-nums">{formatCurrency(shippingCents)}</span>
                      </div>
                      <p className="text-[12px] text-[var(--color-text-muted)] -mt-1">
                        {selectedShippingOption.name}
                      </p>
                    </>
                  )}
                </div>
              </div>
            )}

            <div className="border-t border-[var(--color-surface-border-light)] pt-4 px-4 flex items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)]">
              <span>Powered by</span>
              {resolvedTheme === "light" ? (
                <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5" />
              ) : (
                <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5" />
              )}
            </div>
          </div>

          {/* Spacer for mobile sticky footer */}
          <div className="lg:hidden h-24" />
        </main>
      </div>

      <footer className="hidden lg:flex border-t border-[var(--color-surface-border-light)] p-4 z-50 items-center justify-center gap-1.5 text-xs text-[var(--color-text-muted)]">
        <span>Powered by</span>
        {resolvedTheme === "light" ? (
          <img src="/images/nile-wordmark-dark.png" alt="Nile" className="h-3.5" />
        ) : (
          <img src="/images/nile-wordmark-light.png" alt="Nile" className="h-3.5" />
        )}
      </footer>
    </div>
  );
}
